import React, { useState } from 'react';
import { Info, Camera, Droplet, Clock, Award, ChevronDown, ChevronUp } from 'lucide-react';

interface InstructionStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  details?: string[];
}

export const Instructions: React.FC = () => {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);

  const toggleStep = (index: number) => {
    setExpandedStep(expandedStep === index ? null : index);
  };

  const steps: InstructionStep[] = [
    {
      title: "Camera Detection",
      description: "Use your camera to automatically track water intake",
      icon: <Camera className="h-5 w-5 text-blue-500" />,
      details: [
        "Choose between TensorFlow (local) or Roboflow (cloud) detection",
        "Customize the face and bottle detection areas for better accuracy",
        "When drinking is detected, water intake is automatically added",
        "No data leaves your device with TensorFlow mode"
      ]
    },
    {
      title: "Water Tracking",
      description: "Track your daily water intake progress",
      icon: <Droplet className="h-5 w-5 text-blue-500" />,
      details: [
        "See your current water intake for the day",
        "Visual bottle fills up as you drink more water",
        "Set your daily goal in the account settings",
        "Automatic reset at midnight each day"
      ]
    },
    {
      title: "Reminders",
      description: "Get reminded to drink water regularly",
      icon: <Clock className="h-5 w-5 text-blue-500" />,
      details: [
        "Set custom reminder intervals",
        "Browser notifications when it's time to drink",
        "Pause reminders when needed",
        "Adjust timing in the timer section"
      ]
    },
    {
      title: "Streaks",
      description: "Build a streak by meeting your daily goal",
      icon: <Award className="h-5 w-5 text-blue-500" />,
      details: [
        "Earn a streak day when you meet your daily water goal",
        "View your current streak in the Streaks tab",
        "Streaks reset if you miss a day",
        "Challenge yourself to maintain longer streaks"
      ]
    }
  ];

  return (
    <div className="p-4">
      <div className="flex items-center mb-4">
        <Info className="h-5 w-5 text-blue-500 mr-2" />
        <h3 className="text-lg font-semibold">How to Use This App</h3>
      </div>
      
      <div className="space-y-3">
        {steps.map((step, index) => (
          <div 
            key={index} 
            className="border border-blue-100 rounded-lg overflow-hidden bg-white"
          >
            <button
              onClick={() => toggleStep(index)}
              className="w-full p-3 flex items-center justify-between text-left hover:bg-blue-50 transition-colors"
            >
              <div className="flex items-center">
                <div className="mr-3">{step.icon}</div>
                <div>
                  <h4 className="font-medium">{step.title}</h4>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
              </div>
              {expandedStep === index ? 
                <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                <ChevronDown className="h-4 w-4 text-gray-500" />
              }
            </button>
            
            {expandedStep === index && step.details && (
              <div className="p-3 pt-0 border-t border-blue-100 bg-blue-50">
                <ul className="list-disc pl-5 text-sm space-y-1">
                  {step.details.map((detail, i) => (
                    <li key={i} className="text-gray-700">{detail}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="mt-4 text-xs text-gray-500 italic">
        Tip: Click on each section to expand and learn more details
      </div>
    </div>
  );
};
